import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdCheck, MdVisibility } from 'react-icons/md';
import { THEME, SPRING } from '../theme';
import SwatchPreviewModal from './SwatchPreviewModal';

interface SwatchCardProps {
  url: string;
  label?: string;
  selected: boolean;
  onClick: () => void;
}

const SwatchCard: React.FC<SwatchCardProps> = ({ url, label, selected, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <>
      <motion.div
        className="flex flex-col items-center gap-2"
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
      >
        <button onClick={onClick} className="relative cursor-pointer group">
          <div
            className="w-[88px] h-[88px] rounded-2xl overflow-hidden transition-all duration-300"
            style={{
              boxShadow: selected ? `0 0 0 2.5px ${THEME.primary}, ${THEME.primaryGlow}` : 'none',
              border: selected ? 'none' : '1.5px solid rgba(255,255,255,0.08)',
            }}
          >
            <img
              src={url}
              alt={label || 'swatch'}
              className={`w-full h-full object-cover transition-all duration-300 ${selected ? 'brightness-100 scale-105' : 'brightness-65 group-hover:brightness-90'}`}
              loading="lazy"
            />
          </div>

          {/* Eye icon on hover - top right */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={SPRING.snappy}
                className="absolute -top-1.5 -right-1.5 z-20"
              >
                <motion.div
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOpen(true);
                  }}
                  className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer"
                  style={{
                    background: 'rgba(0,0,0,0.65)',
                    backdropFilter: 'blur(8px)',
                    border: `1.5px solid rgba(255,255,255,0.25)`,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                  }}
                >
                  <MdVisibility size={13} className="text-white/90" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Selected check */}
          <AnimatePresence>
            {selected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={SPRING.snappy}
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: THEME.primary, boxShadow: THEME.primaryGlow }}
              >
                <MdCheck size={12} className="text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </motion.div>

      {/* Preview Modal */}
      <SwatchPreviewModal
        isOpen={previewOpen}
        imageUrl={url}
        label={label}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

export default SwatchCard;
